import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowUpRight, CheckCircle2, Eye, StickyNote } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate, formatNumber, formatRM } from "@/lib/format";
import { Badge, Card, Empty, ErrorNote, Loading, inputClass } from "@/components/portal/Ui";
import { CardDetail } from "@/components/portal/CardDetail";

/** Where a count on a card points: the campaign's own page, with that section filtered. */
export function campaignHref(campaignId, kind, filters = {}) {
  const params = new URLSearchParams();
  if (kind === "invoices") {
    if (filters.status) params.set("inv_status", filters.status);
    params.set("focus", "invoices");
  } else if (kind === "performance") {
    if (filters.creator) params.set("creator", filters.creator);
    if (filters.insight_status) params.set("insight_status", filters.insight_status);
    if (filters.review_status) params.set("review_status", filters.review_status);
    params.set("focus", "submissions");
  }
  const query = params.toString();
  return `/internal/tracker/campaign/${campaignId}${query ? `?${query}` : ""}`;
}


const SORTS = [
  { value: "recent", label: "Newest first" },
  { value: "name", label: "Campaign name" },
  { value: "unpaid", label: "Most unpaid value" },
  { value: "attention", label: "Needs attention first" },
  { value: "reach", label: "Biggest reach" },
];

const STATUSES = ["Upcoming", "Active", "On hold", "Completed", "Archived"];
const DEFAULT_STATUSES = STATUSES.filter((s) => s !== "Archived");

function Tag({ children, tone = "neutral", to, testId, title }) {
  const tones = {
    neutral: "bg-[#F4F1EB] text-[#5d574f] hover:bg-[#E9E4DA]",
    green: "bg-[#EAF6EE] text-[#1B6B3A] hover:bg-[#DCEFE3]",
    red: "bg-[#FDF0EF] text-[#B3261E] hover:bg-[#F8E1DF]",
    orange: "bg-[#FDF0E1] text-[#A85A05] hover:bg-[#F8E3CB]",
    blue: "bg-[#EDF2FB] text-[#2C4D8F] hover:bg-[#DFE8F8]",
  };
  return (
    <Link
      to={to}
      title={title}
      data-testid={testId}
      onClick={(event) => event.stopPropagation()}
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${tones[tone]}`}
    >
      {children}
    </Link>
  );
}

function Stat({ label, value, hint, testId }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">{label}</p>
      <p className="mt-0.5 text-lg font-extrabold tabular" data-testid={testId}>
        {value}
      </p>
      {hint && <p className="text-[11px] body-text">{hint}</p>}
    </div>
  );
}

function attentionScore(row) {
  return (
    row.insights_missing * 2 +
    row.insights_needs_correction * 3 +
    row.invoices_submitted * 2 +
    row.invoices_changes_requested +
    row.invoices_approved
  );
}

function CampaignCard({ row, index, expansion, onQuickLook, onCollapse }) {
  const perf = row.performance || {};
  const expanded = Boolean(expansion);
  const href = campaignHref(row.id);
  const navigate = useNavigate();
  const openCard = (event) => {
    if (event.target.closest("a,button")) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      window.open(href, "_blank", "noopener");
      return;
    }
    navigate(href);
  };
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.3) }}
      onClick={openCard}
      onAuxClick={(event) => event.button === 1 && openCard(event)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" ? navigate(href) : null)}
      data-testid={`tracker-card-${row.id}`}
      className={`group flex cursor-pointer flex-col gap-4 rounded-2xl border bg-white p-5 transition-all hover:shadow-[0_12px_30px_-18px_rgba(17,17,17,0.35)] ${
        expanded ? "lg:col-span-2 2xl:col-span-3" : "hover:-translate-y-0.5"
      } ${
        row.needs_attention
          ? "border-[#F3D0A8]"
          : row.is_settled
            ? "border-[#CBE7D2]"
            : "border-[#E6E1D8]"
      }`}
    >
      <header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-extrabold leading-snug">
              <Link
                to={href}
                onClick={(event) => event.stopPropagation()}
                data-testid={`tracker-card-link-${row.id}`}
                className="hover:text-[#F2811D]"
              >
                {row.name}
              </Link>
            </h3>
            <p className="mt-1 text-xs body-text">{row.client_org_name}</p>
            {row.client_visible === false && (
              <p
                className="mt-0.5 text-[11px] font-semibold text-[#2C4D8F]"
                data-testid={`tracker-hidden-${row.id}`}
              >
                Hidden from client
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <Badge testId={`tracker-status-${row.id}`}>{row.status}</Badge>
            {row.needs_attention ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#A85A05]"
                data-testid={`tracker-attention-${row.id}`}
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Needs attention
              </span>
            ) : row.is_settled ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1B6B3A]"
                data-testid={`tracker-settled-${row.id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Settled
              </span>
            ) : null}
          </div>
        </div>
        <p className="mt-2 text-xs body-text">
          {row.event_date
            ? `Event ${formatDate(row.event_date)}`
            : `${formatDate(row.start_date)} – ${formatDate(row.end_date)}`}
          {row.category ? ` · ${row.category}` : ""}
        </p>
        {row.sticky_note?.body && (
          <p
            className="mt-2.5 flex items-start gap-1.5 rounded-lg bg-[#FDF6E7] px-2.5 py-1.5 text-xs leading-snug text-[#8A5A00]"
            data-testid={`tracker-note-${row.id}`}
          >
            <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {row.sticky_note.body}
          </p>
        )}
      </header>

      {row.insights_required && (
        <section
          className="rounded-xl border border-[#EFEAE1] bg-[#FBFAF7] p-3.5"
          data-testid={`tracker-performance-${row.id}`}
        >
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Reach" value={formatNumber(perf.reach)} testId={`tracker-reach-${row.id}`} />
            <Stat label="Views" value={formatNumber(perf.views)} testId={`tracker-views-${row.id}`} />
            <Stat
              label="Engagements"
              value={formatNumber(perf.engagements)}
              testId={`tracker-engagements-${row.id}`}
            />
          </div>
          <p className="mt-2.5 text-xs body-text" data-testid={`tracker-rate-${row.id}`}>
            {perf.engagement_rate == null
              ? "Engagement rate not available yet"
              : `${perf.engagement_rate}% engagement rate on ${perf.rate_basis}`}
            {" · "}
            {formatNumber(perf.posts)} post{perf.posts === 1 ? "" : "s"} counted
          </p>
          {(row.platforms || []).length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {row.platforms.map((platform) => (
                <span
                  key={platform.platform}
                  data-testid={`tracker-platform-${row.id}-${platform.platform}`}
                  className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#5d574f] ring-1 ring-[#EFEAE1]"
                >
                  {platform.platform} ·{" "}
                  {platform.engagement_share == null ? "—" : `${platform.engagement_share}%`}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">Delivery</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Tag
            tone="blue"
            testId={`tracker-kols-${row.id}`}
            to={campaignHref(row.id, "performance", {})}
          >
            {formatNumber(row.creators_count)} KOLs
          </Tag>
          <Tag
            tone={
              row.expected_posts && row.published_posts >= row.expected_posts ? "green" : "orange"
            }
            testId={`tracker-posts-${row.id}`}
            title="Published vs expected posts"
            to={campaignHref(row.id, "performance", {})}
          >
            {formatNumber(row.published_posts)} / {formatNumber(row.expected_posts)} posts
            {row.expected_posts > 0 && row.published_posts > row.expected_posts
              ? " · over-delivered"
              : ""}
          </Tag>
          {row.insights_required ? (
            <>
              <Tag
                tone="green"
                testId={`tracker-insights-reported-${row.id}`}
                to={campaignHref(row.id, "performance", { insight_status: "reported" })}
              >
                {row.insights_reported} reported
              </Tag>
              {row.insights_missing > 0 && (
                <Tag
                  tone="orange"
                  testId={`tracker-insights-missing-${row.id}`}
                  to={campaignHref(row.id, "performance", { insight_status: "missing" })}
                >
                  {row.insights_missing} missing
                </Tag>
              )}
              {row.insights_needs_correction > 0 && (
                <Tag
                  tone="red"
                  testId={`tracker-insights-correction-${row.id}`}
                  to={campaignHref(row.id, "performance", { insight_status: "needs_correction" })}
                >
                  {row.insights_needs_correction} needs correction
                </Tag>
              )}
              {row.pending_approval_posts > 0 && (
                <Tag
                  tone="orange"
                  testId={`tracker-pending-approval-${row.id}`}
                  to={campaignHref(row.id, "performance", { review_status: "Pending review" })}
                >
                  {row.pending_approval_posts} awaiting approval
                </Tag>
              )}
            </>
          ) : (
            <span className="text-xs body-text" data-testid={`tracker-insights-na-${row.id}`}>
              Insights not required
            </span>
          )}
        </div>
      </section>

      <section className="mt-auto">
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">Money</p>
        <p className="mt-1 font-extrabold tabular" data-testid={`tracker-value-${row.id}`}>
          {formatRM(row.invoiced_sen)}
          <span
            className={`ml-2 text-xs ${row.unpaid_sen ? "font-semibold text-[#A85A05]" : "body-text"}`}
            data-testid={`tracker-unpaid-${row.id}`}
          >
            {row.unpaid_sen ? `${formatRM(row.unpaid_sen)} unpaid` : "nothing outstanding"}
          </span>
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {row.invoices_submitted > 0 && (
            <Tag
              tone="orange"
              testId={`tracker-invoices-submitted-${row.id}`}
              to={campaignHref(row.id, "invoices", { status: "Submitted" })}
            >
              {row.invoices_submitted} submitted
            </Tag>
          )}
          {row.invoices_changes_requested > 0 && (
            <Tag
              tone="red"
              testId={`tracker-invoices-changes-${row.id}`}
              to={campaignHref(row.id, "invoices", { status: "Changes requested" })}
            >
              {row.invoices_changes_requested} to correct
            </Tag>
          )}
          <Tag
            tone="blue"
            testId={`tracker-invoices-approved-${row.id}`}
            to={campaignHref(row.id, "invoices", { status: "Approved" })}
          >
            {row.invoices_approved} approved
          </Tag>
          <Tag
            tone="green"
            testId={`tracker-invoices-paid-${row.id}`}
            to={campaignHref(row.id, "invoices", { status: "Paid" })}
          >
            {row.invoices_paid} paid
          </Tag>
        </div>
      </section>

      {expanded ? (
        <CardDetail
          row={row}
          onCollapse={onCollapse}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onQuickLook();
            }}
            data-testid={`tracker-quick-look-${row.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#6b6660] hover:text-[#111111]"
          >
            <Eye className="h-3.5 w-3.5" />
            Quick look
          </button>
          <Link
            to={href}
            onClick={(event) => event.stopPropagation()}
            data-testid={`tracker-open-${row.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#F2811D] opacity-0 transition-opacity group-hover:opacity-100"
          >
            Open campaign view <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </motion.article>
  );
}

export default function TrackerOverview() {
  const [expansion, setExpansion] = useState(null);
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [statuses, setStatuses] = useState(() => {
    const raw = (params.get("statuses") || "").split(",").filter((s) => STATUSES.includes(s));
    return raw.length ? raw : DEFAULT_STATUSES;
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/internal/tracker", {
        params: { statuses: statuses.join(",") },
      });
      setRows(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setRows((prev) => prev || []);
    }
  }, [statuses]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const sorted = STATUSES.filter((s) => statuses.includes(s));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        if (sorted.join(",") !== DEFAULT_STATUSES.join(",")) next.set("statuses", sorted.join(","));
        else next.delete("statuses");
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses, setParams]);

  const toggleStatus = (status) =>
    setStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status]
    );

  const visible = useMemo(() => {
    if (!rows) return [];
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? rows.filter((r) =>
          `${r.name} ${r.client_org_name || ""} ${r.status} ${r.category || ""}`
            .toLowerCase()
            .includes(needle)
        )
      : [...rows];
    const sorters = {
      recent: () => 0,
      name: (a, b) => a.name.localeCompare(b.name),
      unpaid: (a, b) => b.unpaid_sen - a.unpaid_sen,
      attention: (a, b) => attentionScore(b) - attentionScore(a),
      reach: (a, b) => (b.performance?.reach || 0) - (a.performance?.reach || 0),
    };
    return filtered.sort(sorters[sort]);
  }, [rows, search, sort]);

  if (rows === null) return <Loading label="Loading campaign roll-up…" />;

  return (
    <div data-testid="tracker-overview">
      <Card className="mb-5" testId="tracker-overview-filters">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr]">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaign, client, category or status…"
            className={inputClass}
            data-testid="tracker-search"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className={inputClass}
            data-testid="tracker-sort"
          >
            {SORTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="tracker-status-filter">
          {STATUSES.map((status) => {
            const on = statuses.includes(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatus(status)}
                aria-pressed={on}
                data-testid={`tracker-status-toggle-${status.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                  on
                    ? "bg-[#111111] text-white"
                    : "border border-[#E6E1D8] bg-white text-[#6b6660] hover:border-[#111111]"
                }`}
              >
                {status}
              </button>
            );
          })}
          <span className="ml-auto text-xs body-text" data-testid="tracker-match-count">
            {visible.length} campaign{visible.length === 1 ? "" : "s"} match
          </span>
        </div>
      </Card>
      <ErrorNote message={error} onRetry={load} />

      {visible.length === 0 ? (
        <Empty
          testId="tracker-overview-empty"
          title="No campaigns to roll up yet"
          body="Create a campaign and assign KOLs — compiled performance, insight coverage and invoice value appear here."
        />
      ) : (
        <>
          <div className="grid items-start gap-4 lg:grid-cols-2 2xl:grid-cols-3" data-testid="tracker-card-grid">
            {visible.map((row, index) => (
              <CampaignCard
                key={row.id}
                row={row}
                index={index}
                expansion={expansion === row.id ? row.id : null}
                onQuickLook={() => setExpansion(row.id)}
                onCollapse={() => setExpansion(null)}
              />
            ))}
          </div>
          <p className="mt-4 text-xs body-text">
            Every count links into that campaign's page with the matching section filtered — open it
            in a new tab to compare campaigns side by side. "Quick look" reads the campaign in place.
            Totals sum the latest insight per submission; figures marked "needs correction" stay out
            of them, and UGC campaigns carry no posting or insight requirement.
          </p>
        </>
      )}
    </div>
  );
}
